import { useState } from 'react';
import type { FaqItem } from '../../lib/faq';
import ParkingMap from './ParkingMap';

/**
 * 空欄のチャットの手前に置く「案内」：カテゴリ → 質問 → 答え。
 * 🟥 答えは園が承認した「よくある質問」をそのまま出す（AIに考えさせない＝待ち時間なし・ぶれなし）。
 *    解決しなければ「あおいに聞く（自由に入力）」か「園に聞く」へ。
 */
type Group = { key: string; label: string; icon: string; items: FaqItem[] };

export default function FaqGuide({
  groups, initialCat, accent, onAskAoi, onAskGarden, garden,
}: {
  garden: string;
  groups: Group[];
  initialCat?: string | null;
  accent: string;
  onAskAoi: (text?: string) => void;
  onAskGarden: (question: string) => void;
}) {
  const [cat, setCat] = useState<string | null>(initialCat && groups.some((g) => g.key === initialCat) ? initialCat : null);
  const [q, setQ] = useState<FaqItem | null>(null);
  const [solved, setSolved] = useState(false);
  const group = groups.find((g) => g.key === cat);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
      <div className="flex items-start gap-3 mb-4">
        <img src="/images/aoi.jpg" alt="あおい" className="w-11 h-11 rounded-full object-cover object-top border-2 border-white shadow flex-shrink-0 bg-white" />
        <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 text-base text-gray-800 leading-relaxed">
          {q ? 'こちらがお答えです。' : group ? `「${group.label}」で、よく聞かれることです。` : 'なにが知りたいですか？ 押すと、すぐお答えします。'}
        </div>
      </div>

      {!group && (
        <div className="grid grid-cols-2 gap-3">
          {groups.map((g) => (
            <button key={g.key} onClick={() => setCat(g.key)}
              className="flex flex-col items-start gap-2 bg-white border-2 border-gray-200 rounded-2xl p-4 text-left active:scale-[0.98] transition-transform cursor-pointer">
              <i className={`${g.icon} text-3xl`} style={{ color: accent }}></i>
              <span className="text-base font-bold text-gray-800 leading-snug">{g.label}</span>
              <span className="text-xs text-gray-500">{g.items.length}件</span>
            </button>
          ))}
        </div>
      )}

      {group && !q && (
        <div className="flex flex-col gap-2">
          {group.items.map((it) => (
            <button key={it.question} onClick={() => { setQ(it); setSolved(false); }}
              className="w-full text-left text-base text-gray-800 bg-white border-2 border-gray-200 rounded-2xl px-4 py-3 active:scale-[0.99] transition-transform cursor-pointer">
              {it.question}
            </button>
          ))}
          <button onClick={() => setCat(null)} className="mt-2 text-base font-bold text-gray-500 py-2 cursor-pointer">← ほかのカテゴリ</button>
        </div>
      )}

      {q && (
        <div>
          <div className="ml-auto max-w-[85%] w-fit rounded-2xl rounded-tr-sm px-4 py-3 text-base text-white mb-3" style={{ background: accent }}>{q.question}</div>
          <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 text-base text-gray-800 leading-relaxed whitespace-pre-wrap">{q.answer}</div>
          {/* 駐車場の答えには、地図とパーキングの一覧を添える */}
          {(cat === '駐車場' || q.category === '駐車場') && <ParkingMap garden={garden} accent={accent} />}
          {solved ? (
            <p className="mt-4 text-base text-gray-700">よかったです。当日、お待ちしています。</p>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-2">
              <button onClick={() => setSolved(true)} className="rounded-2xl py-3 text-base font-bold text-white active:scale-[0.98] transition-transform cursor-pointer" style={{ background: accent }}>解決しました</button>
              <button onClick={() => { setQ(null); }} className="rounded-2xl py-3 text-base font-bold text-gray-700 bg-white border-2 border-gray-200 active:scale-[0.98] transition-transform cursor-pointer">ほかの質問を見る</button>
              <button onClick={() => onAskAoi(`${q.question}（さっきの答えで分からなかったところ）`)} className="rounded-2xl py-3 text-base text-gray-700 bg-white border-2 border-gray-200 active:scale-[0.98] transition-transform cursor-pointer">あおいに続けて聞く</button>
              <button onClick={() => onAskGarden(q.question)} className="rounded-2xl py-3 text-base text-gray-700 bg-white border-2 border-gray-200 active:scale-[0.98] transition-transform cursor-pointer">園に聞く</button>
            </div>
          )}
          {solved && <button onClick={() => { setQ(null); setCat(null); setSolved(false); }} className="mt-3 text-base font-bold text-gray-500 py-2 cursor-pointer">ほかのことも見る</button>}
        </div>
      )}

      <button onClick={() => onAskAoi()} className="mt-6 w-full text-center text-base text-gray-500 underline underline-offset-4 py-2 cursor-pointer">ここに無いことを、あおいに自由に聞く</button>
    </div>
  );
}
